import type { NumericTarget } from "@corpuscomplete/core/common/numeric-target";
import { Progress } from "@corpuscomplete/web-ui/components/progress";

type ProgressTarget = NumericTarget.Info<"exact" | "between" | "atLeast" | "atMost">;

export type NumericTargetProgressProps = {
  value: number;
  target: ProgressTarget;
  className?: string;
};

function percent(value: number, target: number) {
  if (!Number.isFinite(value) || !Number.isFinite(target) || target <= 0) {
    return 0;
  }
  return (value / target) * 100;
}

function progressIndicatorClass(value: number, target: ProgressTarget) {
  switch (target.type) {
    case "exact":
      return value > target.value ? "bg-destructive" : undefined;
    case "between":
      return value > target.max
        ? "bg-destructive"
        : value >= target.min
          ? "bg-emerald-600"
          : undefined;
    case "atLeast":
      return value >= target.value ? "bg-emerald-600" : undefined;
    case "atMost":
      return value > target.value ? "bg-destructive" : "bg-emerald-600";
  }
}

function targetScale(target: ProgressTarget) {
  return target.type === "between" ? target.max : target.value;
}

export function NumericTargetProgress({ value, target, className }: NumericTargetProgressProps) {
  const progressValue = percent(value, targetScale(target));
  const bandStart =
    target.type === "between" ? Math.min(Math.max(percent(target.min, target.max), 0), 100) : 0;
  const bandWidth = target.type === "between" ? Math.max(100 - bandStart, 0) : 0;

  return (
    <div className={className}>
      <div className="relative">
        <Progress
          value={progressValue}
          indicatorClassName={progressIndicatorClass(value, target)}
        />
        {target.type === "between" && (
          <div
            className="pointer-events-none absolute top-0 h-1 rounded-sm bg-emerald-500/35"
            style={{ left: `${bandStart}%`, width: `${bandWidth}%` }}
          />
        )}
      </div>
    </div>
  );
}
